import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Row, Col } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import clsx from 'classnames';
import { useTeamInfo } from '../../team-id-context';
import CounterpartyMembersListContainer from './CounterpartyMembersListContainer';
import { TeamMember } from '../document-wizard/document-editor/DocumentEditorTypes';
import { TitleFormattedMessage } from '../titleFormattedMessage';
import RemoveCounterpartyModal from '../document-sidebars/document-team-sidebar/components/removeCounterpartyModal';
import { useDealId } from '../../deal-id-context';
import styles from './styles.module.scss';

interface CounterpartyPanelProps {
  isAdmin: boolean;
  hasCreatorInvitePermission: boolean;
  canInviteCounterparty: boolean;
  isDealShared: boolean;
  counterpartyTeam: TeamMember[];
  isDealAccepted: boolean;
  counterpartyInvitedEmail: string;
  counterpartyAdminUserId: string;
  refetchDealData: () => Promise<void>;
  onShowInviteCounterpartyModal: () => void;
  onShowSubscriptionRequiredModal: () => void;
}

const CounterpartyPanel = ({
  isAdmin,
  hasCreatorInvitePermission,
  canInviteCounterparty,
  isDealShared,
  counterpartyTeam,
  isDealAccepted,
  counterpartyInvitedEmail,
  counterpartyAdminUserId,
  refetchDealData,
  onShowInviteCounterpartyModal,
  onShowSubscriptionRequiredModal,
}: CounterpartyPanelProps) => {
  const { teamId } = useTeamInfo();
  const { formatMessage } = useIntl();
  const dealId = useDealId();
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const handleInviteCounterpartyButton = canInviteCounterparty
    ? onShowInviteCounterpartyModal
    : onShowSubscriptionRequiredModal;

  const handleShowRemoveModal = useCallback(() => {
    setShowRemoveModal(true);
  }, []);

  const handleRemoveSuccess = useCallback(async () => {
    await refetchDealData();
    setShowRemoveModal(false);
  }, [refetchDealData]);

  return (
    <>
      <Container fluid="md" className="pb-md-3 d-flex flex-column h-100">
        {isDealAccepted && hasCreatorInvitePermission && (
          <Row className={clsx('flex-shrink-0', styles.removeButtonRow)}>
            <Col className="d-flex justify-content-end">
              <Button
                variant="outline-danger"
                className={styles.handleParticipantsButton}
                onClick={handleShowRemoveModal}
              >
                <TitleFormattedMessage id="cta.remove" defaultMessage="Remove" />
              </Button>
            </Col>
          </Row>
        )}
        {!isDealShared ? (
          <Container className="mt-5 mb-3 d-flex justify-content-center flex-column align-items-center">
            <Row className="m-4">
              <FormattedMessage
                id="cta.no-counterparty-invited"
                defaultMessage="No counterparty has been invited yet"
              />
            </Row>
            {(isAdmin || hasCreatorInvitePermission) && (
              <Row>
                <Button
                  variant="primary"
                  onClick={handleInviteCounterpartyButton}
                  className={styles.handleParticipantsButton}
                >
                  <TitleFormattedMessage id="cta.invite-counterparty" defaultMessage="Invite Counterparty" />
                </Button>
              </Row>
            )}
          </Container>
        ) : (
          <Row className={clsx('flex-1 min-h-0', styles.scrollableRow)}>
            <Col className={clsx(styles.teamMembersListContainer, styles.contentMemberList)}>
              <CounterpartyMembersListContainer
                counterpartyTeam={counterpartyTeam}
                isDealAccepted={isDealAccepted}
                counterpartyInvitedEmail={counterpartyInvitedEmail}
                counterpartyAdminUserId={counterpartyAdminUserId}
                hasCreatorInvitePermission={hasCreatorInvitePermission}
              />
            </Col>
          </Row>
        )}
      </Container>

      {/* Remove Counterparty Modal */}
      {showRemoveModal && (
        <RemoveCounterpartyModal
          dealId={dealId}
          teamId={teamId}
          userId={counterpartyAdminUserId}
          onSuccess={handleRemoveSuccess}
          hideButton={true}
        />
      )}
    </>
  );
};

export default CounterpartyPanel;
